import css from '@styled-system/css';
import { startOfDay } from 'date-fns';
import styled from 'styled-components';

import { GenericError } from '../../common/Error/GenericError';
import { formatDate } from '../../common/helpers/formatDate.helper';
import { translate } from '../../common/helpers/translate.helper';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../common/ui/Box';

import { OfferChatMessage } from './OfferChatMessage';
import { StyledMessage } from './StyledMessage';
import { useMessages } from './useMessages';
import { useScrollToBottom } from './useScrollToBottom';

const Day = styled(Box)(
    css({
        display: 'inline-block',
        width: '60%',
        margin: 'auto',
        textAlign: 'center',
        borderRadius: 4,
        padding: 8,
        paddingTop: 24,
        fontSize: 12,
        fontWeight: 600,
        color: 'foreground.muted',
    })
);

const EmptyMessage = styled(StyledMessage)(
    css({
        textAlign: 'center',
        margin: '20px auto',
        boxShadow: 'none',
        height: '100%',
    })
);

export const OfferChatMessages = ({ offer }) => {
    const { messages, isPending, isError } = useMessages(offer);
    const messageBoxRef = useScrollToBottom({
        dependencies: messages,
    });

    if (isPending) {
        return (
            <Box display="flex" height="100%" flexDirection="column" margin={24}>
                <LoadingIndicator />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box display="flex" height="100%" flexDirection="column" margin={24}>
                <GenericError variant="basic" />
            </Box>
        );
    }

    if (!messages.length) {
        return <EmptyMessage>{translate('order.emptymessages')}</EmptyMessage>;
    }

    const messageGroupedByDay = messages.reduce((acc, message) => {
        const dateCreated = startOfDay(new Date(message.dateCreated)).toISOString();
        acc[dateCreated] = acc[dateCreated] || [];
        acc[dateCreated].push(message);
        return acc;
    }, []);

    return (
        <Box ref={messageBoxRef} overflow="auto" height="100%">
            {Object.entries(messageGroupedByDay).map(([dateCreated, messagesOfTheDay]) => (
                <Box display="flex" key={dateCreated} flexDirection="column">
                    <Day>{formatDate({ date: dateCreated })}</Day>
                    {messagesOfTheDay.map((message) => (
                        <OfferChatMessage key={message.id} message={message} />
                    ))}
                </Box>
            ))}
        </Box>
    );
};
